import { AuthError } from "firebase/auth";

interface FirebaseErrorInfo {
  userMessage: string;
  loggedMessage: string;
  severity: "low" | "medium" | "high";
}

/**
 * Sanitizes Firebase error messages to prevent sensitive data leakage
 * Maps Firebase error codes to user-friendly messages with severity levels
 */
export function sanitizeFirebaseError(error: AuthError): {
  sanitizedMessage: string;
  loggedMessage: string;
  errorCode: string;
  severity: "low" | "medium" | "high";
} {
  const errorCode = error.code;

  // Map common Firebase errors to safe, user-friendly messages with severity
  const errorMap: Record<string, FirebaseErrorInfo> = {
    "auth/popup-blocked": {
      userMessage:
        "Pop-up was blocked by your browser. Please allow pop-ups for this site.",
      loggedMessage: "Popup blocked by browser",
      severity: "low",
    },
    "auth/popup-closed-by-user": {
      userMessage: "Sign-in cancelled.",
      loggedMessage: "User closed popup",
      severity: "low",
    },
    "auth/cancelled-popup-request": {
      userMessage: "Sign-in cancelled.",
      loggedMessage: "Popup request cancelled",
      severity: "low",
    },
    "auth/account-exists-with-different-credential": {
      userMessage:
        "An account already exists with this email using a different sign-in method.",
      loggedMessage: "Account exists with different auth provider",
      severity: "medium",
    },
    "auth/invalid-email": {
      userMessage: "Invalid email address.",
      loggedMessage: "Invalid email format provided",
      severity: "low",
    },
    "auth/user-disabled": {
      userMessage: "This account has been disabled.",
      loggedMessage: "Attempted login to disabled account",
      severity: "high",
    },
    "auth/user-not-found": {
      userMessage: "No account found with this email.",
      loggedMessage: "Login attempt for non-existent user",
      severity: "low",
    },
    "auth/wrong-password": {
      userMessage: "Incorrect password.",
      loggedMessage: "Invalid password attempt",
      severity: "medium",
    },
    "auth/network-request-failed": {
      userMessage: "Network error. Please check your connection.",
      loggedMessage: "Network request failed during auth",
      severity: "medium",
    },
    "auth/too-many-requests": {
      userMessage: "Too many failed attempts. Please try again later.",
      loggedMessage: "Rate limit exceeded for auth attempts",
      severity: "high",
    },
    "auth/operation-not-allowed": {
      userMessage: "This sign-in method is not enabled.",
      loggedMessage: "Attempted to use disabled auth method",
      severity: "high",
    },
    "auth/email-already-in-use": {
      userMessage:
        "An account with this email already exists. Please log in instead.",
      loggedMessage: "Registration attempt with existing email",
      severity: "low",
    },
    "auth/weak-password": {
      userMessage: "Password is too weak. Please use a stronger password.",
      loggedMessage: "Weak password rejected",
      severity: "low",
    },
    "auth/invalid-credential": {
      userMessage: "Invalid credentials. Please check your email and password.",
      loggedMessage: "Invalid credentials provided",
      severity: "medium",
    },
  };

  const errorInfo = errorMap[errorCode] || {
    userMessage: "An error occurred during sign-in. Please try again.",
    loggedMessage: `Unknown auth error: ${errorCode}`,
    severity: "high" as const,
  };

  return {
    sanitizedMessage: errorInfo.userMessage,
    loggedMessage: errorInfo.loggedMessage,
    errorCode,
    severity: errorInfo.severity,
  };
}
